import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { User, Lock, Bell, LogOut, Image } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SettingsPage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profileData, setProfileData] = useState({
    full_name: '',
    avatar_url: ''
  });
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  useEffect(() => {
    if (user) {
      setProfileData({
        full_name: user.full_name || '',
        avatar_url: user.avatar_url || ''
      });
    }
  }, [user]);

  const handleUpdateProfile = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/auth/profile`, {
        full_name: profileData.full_name,
        avatar_url: profileData.avatar_url
      });
      toast.success('Profile updated successfully');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', avatarFile);
      
      const response = await axios.post(`${API}/me/avatar/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setProfileData({...profileData, avatar_url: response.data.avatar_url});
      setAvatarFile(null);
      toast.success('Avatar uploaded successfully');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to upload avatar');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveAvatar = async () => {
    setLoading(true);
    try {
      await axios.patch(`${API}/auth/profile`, { remove_avatar: true });
      toast.success('Avatar removed');
      window.location.reload();
    } catch (error) {
      toast.error('Failed to remove avatar');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.new_password !== passwordData.confirm_password) {
      toast.error('Passwords do not match');
      return;
    }
    if (passwordData.new_password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      toast.success('Password changed successfully');
      setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md w-full mx-4">
          <CardContent className="p-6 text-center">
            <p className="mb-4">Please login to access settings</p>
            <Button onClick={() => navigate('/login')}>Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-6">
          <button onClick={() => navigate('/')} className="text-blue-600 hover:underline mb-2">← Back to Home</button>
          <h1 className="text-3xl font-bold">Settings</h1>
        </div>

        <div className="space-y-6">
          {/* Profile Settings */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="w-5 h-5" />
                Profile Information
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Full Name</label>
                <Input
                  value={profileData.full_name}
                  onChange={(e) => setProfileData({...profileData, full_name: e.target.value})}
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2 flex items-center gap-2">
                  <Image className="w-4 h-4" />
                  Avatar
                </label>
                
                {/* Option 1: Upload File */}
                <div className="mb-3">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAvatarFile(e.target.files[0])}
                    className="text-sm"
                  />
                  {avatarFile && (
                    <Button onClick={handleAvatarUpload} disabled={uploading} size="sm" className="mt-2">
                      {uploading ? 'Uploading...' : 'Upload Image'}
                    </Button>
                  )}
                </div>
                
                {/* Option 2: Use URL */}
                <Input
                  value={profileData.avatar_url}
                  onChange={(e) => setProfileData({...profileData, avatar_url: e.target.value})}
                  placeholder="Or paste image URL"
                />
                {profileData.avatar_url && (
                  <div className="mt-2 flex items-center gap-3">
                    <img src={profileData.avatar_url} className="w-16 h-16 rounded-full object-cover" alt="Preview" />
                    <span className="text-xs text-muted-foreground">Preview</span>
                  </div>
                )}
              </div>
              <Button onClick={handleUpdateProfile} disabled={loading}>
                {loading ? 'Saving...' : 'Save Profile'}
              </Button>
              <Button onClick={() => handleRemoveAvatar()} disabled={loading} variant="outline" className="mt-2">
                Remove Avatar
              </Button>
            </CardContent>
          </Card>

          {/* Password */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Lock className="w-5 h-5" />
                Change Password
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="text-sm font-medium block mb-2">Current Password</label>
                <Input
                  type="password"
                  value={passwordData.current_password}
                  onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                  autoComplete="current-password"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">New Password</label>
                <Input
                  type="password"
                  value={passwordData.new_password}
                  onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label className="text-sm font-medium block mb-2">Confirm New Password</label>
                <Input
                  type="password"
                  value={passwordData.confirm_password}
                  onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                  autoComplete="new-password"
                />
              </div>
              <Button onClick={handleChangePassword} disabled={loading}>
                {loading ? 'Changing...' : 'Change Password'}
              </Button>
            </CardContent>
          </Card>

          {/* Notifications */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Bell className="w-5 h-5" />
                Notifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">Notification preferences coming soon</p>
            </CardContent>
          </Card>

          {/* Logout */}
          <Card>
            <CardContent className="p-6">
              <Button variant="destructive" onClick={logout} className="w-full">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
